<script setup>
import { watch, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useContactNotes } from 'dashboard/composables/useContactNotes';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactNoteItem from 'next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const contactId = computed(() => props.contactId);
const noteContent = ref('');
const shouldShowCreateModal = ref(false);

const {
  allNotes,
  isFetchingContactNotes: isFetchingNotes,
  isCreatingContactNote: isCreatingNote,
} = useContactNotes(contactId);

// Un contacto con muchas conversaciones acumula demasiadas notas privadas
// para un panel lateral: se muestran las más recientes y el resto bajo
// demanda.
const VISIBLE_NOTES = 10;
const showAllNotes = ref(false);

const notes = computed(() =>
  showAllNotes.value ? allNotes.value : allNotes.value.slice(0, VISIBLE_NOTES)
);

const hiddenNotesCount = computed(
  () => allNotes.value.length - notes.value.length
);

const goToConversation = displayId => {
  if (!displayId) return;
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: displayId },
  });
};

const getWrittenBy = ({ user } = {}) => {
  const currentUserId = currentUser.value?.id;
  return user?.id === currentUserId
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : user?.name || t('CONVERSATION.BOT');
};

const openCreateModal = () => {
  if (!contactId.value) {
    return;
  }

  noteContent.value = '';
  shouldShowCreateModal.value = true;
};

const closeCreateModal = () => {
  shouldShowCreateModal.value = false;
  noteContent.value = '';
};

const onAdd = async () => {
  if (!contactId.value || !noteContent.value || isCreatingNote.value) {
    return;
  }

  await store.dispatch('contactNotes/create', {
    content: noteContent.value,
    contactId: contactId.value,
  });
  noteContent.value = '';
  closeCreateModal();
};

const onDelete = noteId => {
  if (!contactId.value || !noteId) {
    return;
  }

  store.dispatch('contactNotes/delete', {
    noteId,
    contactId: contactId.value,
  });
};

const keyboardEvents = {
  '$mod+Enter': {
    action: onAdd,
    allowOnFocusedInput: true,
  },
};

useKeyboardEvents(keyboardEvents);

watch(
  contactId,
  newContactId => {
    closeCreateModal();
    showAllNotes.value = false;
    if (newContactId) {
      store.dispatch('contactNotes/get', { contactId: newContactId });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div class="px-4 pt-3 pb-2">
      <NextButton
        ghost
        xs
        icon="i-lucide-plus"
        :label="$t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ADD_NOTE')"
        :disabled="!contactId || isFetchingNotes"
        @click="openCreateModal"
      />
    </div>

    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="notes.length">
      <div class="flex flex-col max-h-[300px] overflow-y-auto">
        <div
          v-for="note in notes"
          :key="note.key"
          class="px-4 py-4 border-b border-n-strong last:border-b-0"
        >
          <ContactNoteItem
            class="!border-b-0 !pb-0"
            :note="note"
            :written-by="getWrittenBy(note)"
            :allow-delete="note.isContactNote"
            collapsible
            @delete="onDelete"
          />
          <button
            v-if="!note.isContactNote"
            type="button"
            class="inline-flex items-center gap-1 mt-1.5 text-[11px] font-medium text-n-slate-10 hover:text-n-blue-text transition-colors"
            @click="goToConversation(note.conversationDisplayId)"
          >
            <span class="i-lucide-message-square size-3" />
            {{
              t('CONTACTS_LAYOUT.SIDEBAR.NOTES.FROM_CONVERSATION', {
                id: note.conversationDisplayId,
              })
            }}
          </button>
        </div>
      </div>
      <button
        v-if="hiddenNotesCount > 0 || showAllNotes"
        type="button"
        class="w-full px-4 py-2.5 text-xs font-medium text-center border-t text-n-blue-text border-n-strong hover:bg-n-alpha-1 transition-colors"
        @click="showAllNotes = !showAllNotes"
      >
        <template v-if="showAllNotes">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SHOW_LESS') }}
        </template>
        <template v-else>
          {{
            t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SHOW_ALL', {
              count: hiddenNotesCount,
            })
          }}
        </template>
      </button>
    </template>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.CONVERSATION_EMPTY_STATE') }}
    </p>

    <woot-modal
      v-model:show="shouldShowCreateModal"
      :on-close="closeCreateModal"
      :close-on-backdrop-click="false"
      class="!items-start [&>div]:!top-12 [&>div]:sticky"
    >
      <div class="flex w-full flex-col gap-6 px-6 py-6">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ADD_NOTE') }}
        </h3>
        <Editor
          v-model="noteContent"
          focus-on-mount
          :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.PLACEHOLDER')"
          class="[&>div]:!border-transparent [&>div]:px-4 [&>div]:py-4"
        />
        <div class="flex items-center justify-end gap-3">
          <NextButton
            solid
            blue
            :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
            :is-loading="isCreatingNote"
            :disabled="!noteContent || isCreatingNote"
            @click="onAdd"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>
